/**
 * Service cost detail, as a modal over the Unit Costs table.
 *
 * A modal rather than a route because the table is what's being scanned: you
 * open a service, understand it, close it, keep comparing. Navigating away
 * loses the sort, filter and scroll position every time.
 *
 * Ordering is deliberate. Someone arriving here from a suspicious
 * cost-per-million wants the *explanation* before the specifications, so the
 * usage-type split comes first: a service reading $677,467 per million is
 * almost never expensive per call, it is usually paying for provisioned
 * concurrency that bills whether or not anything invokes it.
 *
 * Follows the ModalShell markup used by AdminUsers/ServiceCatalog (fixed
 * overlay, click-outside to close) rather than introducing a dialog library.
 */
import { useEffect, useMemo } from 'react';
import { AlertTriangle, ExternalLink, X } from 'lucide-react';

import { useUnitCostDetail } from '@/hooks/useUnitCosts';
import type {
  ResourceSpecRow,
  ServiceCatalogRow,
  UnitCostPeriod,
  UsageTypeRow,
} from '@/api/unitCosts';

/** Tag keys teams actually use for ownership, most specific first. */
const OWNER_TAG_KEYS = ['owner', 'Owner', 'team', 'Team', 'created-by', 'CreatedBy'];

interface Props {
  serviceSlug: string | null;
  env?: string;
  period: UnitCostPeriod;
  onClose: () => void;
}

function usd(value: string | null | undefined): string {
  if (value == null) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  if (n === 0) return '$0.00';
  if (n < 0.01) return `$${n.toFixed(6)}`;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Cost of 1000 calls, with precision that follows magnitude — the fleet spans
 * $475.90 per thousand down to $0.002052, and no single format reads well
 * across both.
 */
function per1000(value: string | null | undefined): string {
  if (value == null) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  if (n >= 1) return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (n >= 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(6)}`;
}

/** Fixed 8 decimals: a currency format renders every real value as $0.00. */
function perInvocation(value: string | null | undefined): string {
  if (value == null) return '—';
  const n = Number(value);
  return Number.isNaN(n) ? '—' : `$${n.toFixed(8)}`;
}

function qty(value: string): string {
  const n = Number(value);
  return Number.isNaN(n) ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/**
 * Usage types are colour-coded by what they mean, not merely to separate
 * categories: reserved capacity is amber because it is the finding, compute
 * blue, per-request green. An unrecognised type falls back to slate so a new
 * one cannot quietly claim the warning colour.
 */
function bandFor(usageType: string): { bar: string; dot: string; idle: boolean } {
  const t = usageType.toLowerCase();
  if (t.includes('provisioned'))
    return { bar: 'bg-amber-400', dot: 'bg-amber-400', idle: true };
  if (t.includes('gb-second') || t.includes('gb-s'))
    return { bar: 'bg-brand-500', dot: 'bg-brand-500', idle: false };
  if (t.includes('request')) return { bar: 'bg-emerald-500', dot: 'bg-emerald-500', idle: false };
  if (t.includes('bytes') || t.includes('datatransfer'))
    return { bar: 'bg-slate-400', dot: 'bg-slate-400', idle: false };
  return { bar: 'bg-slate-300', dot: 'bg-slate-300', idle: false };
}

/**
 * What the `Quantity` column is actually counting.
 *
 * Cost Explorer hands back a bare number per usage type; the unit is only
 * implied by the type name. That is how a quantity of 3.5 million next to a
 * $59 charge ends up looking like a bug — those are GB-seconds (memory
 * reserved x billed duration), not invocations, and only the `Request` row is
 * ever a call count.
 */
function unitFor(usageType: string, usageUnit: string | null): string {
  const t = usageType.toLowerCase();
  if (t.includes('provisioned')) return 'GB-seconds reserved';
  if (t.includes('gb-second') || t.includes('gb-s')) return 'GB-seconds';
  if (t.includes('request')) return 'requests';
  if (t.includes('bytes') || t.includes('datatransfer')) return 'GB transferred';
  if (t.includes('storage')) return 'GB-months';
  return usageUnit ?? '';
}

/**
 * Cost / quantity — the effective rate AWS charged.
 *
 * Shown because it makes each row check itself: quantity x rate equals cost,
 * and the rate should match Lambda's published price for that usage type. When
 * someone asks whether these figures can be trusted, this is the column that
 * answers it without leaving the page.
 */
function rate(cost: string, quantity: string): string {
  const c = Number(cost);
  const q = Number(quantity);
  if (!Number.isFinite(c) || !Number.isFinite(q) || q === 0) return '—';
  const r = c / q;
  if (r === 0) return '$0';
  if (r >= 0.01) return `$${r.toFixed(4)}`;
  return `$${r.toFixed(10).replace(/0+$/, '')}`;
}

function cloudWatchLogsUrl(fn: string, region: string | null): string {
  const r = region || 'us-east-1';
  // CloudWatch encodes the log-group path with $252F for each slash.
  const group = encodeURIComponent(`/aws/lambda/${fn}`).replace(/%2F/g, '$252F');
  return `https://${r}.console.aws.amazon.com/cloudwatch/home?region=${r}#logsV2:log-groups/log-group/${group}`;
}

function lambdaConsoleUrl(fn: string, region: string | null): string {
  const r = region || 'us-east-1';
  return `https://${r}.console.aws.amazon.com/lambda/home?region=${r}#/functions/${encodeURIComponent(fn)}`;
}

function datadogLogsUrl(fn: string): string {
  return `https://app.datadoghq.com/logs?query=${encodeURIComponent(`service:${fn}`)}&from_ts=now-14d&to_ts=now&live=false`;
}

function datadogMetricsUrl(fn: string): string {
  return `https://app.datadoghq.com/metric/explorer?filter=${encodeURIComponent(`functionname:${fn}`)}`;
}

function Kpi({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="rounded-xl border border-slate-200/60 p-3 dark:border-slate-700">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p
        className={`mt-1 text-lg font-semibold tabular-nums ${
          muted ? 'text-slate-400' : 'text-slate-900 dark:text-slate-100'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function LinkRow({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 text-sm text-brand-600 hover:underline dark:text-brand-400"
    >
      <ExternalLink className="h-3.5 w-3.5 flex-none" />
      {children}
    </a>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h4>
      {hint && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function UsageTypes({ rows }: { rows: UsageTypeRow[] }) {
  const total = rows.reduce((s, r) => s + Number(r.cost_usd), 0);
  const share = (r: UsageTypeRow) => (total > 0 ? (Number(r.cost_usd) / total) * 100 : 0);
  const idleShare = rows.filter((r) => bandFor(r.usage_type).idle).reduce((s, r) => s + share(r), 0);

  if (rows.length === 0) {
    return <p className="text-sm text-slate-400">No usage recorded in this window.</p>;
  }

  return (
    <>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        {rows
          .filter((r) => share(r) > 0.05)
          .map((r) => (
            <div
              key={r.usage_type}
              className={bandFor(r.usage_type).bar}
              style={{ width: `${share(r)}%` }}
              title={`${r.usage_type} — ${share(r).toFixed(1)}%`}
            />
          ))}
      </div>

      {idleShare > 50 && (
        <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" />
          <span>
            <strong>{idleShare.toFixed(0)}% of this service&apos;s cost is reserved capacity</strong>,
            billed whether or not anything invokes it. The cost per invocation reflects idle
            provisioning rather than expensive calls — the saving is in the configuration, not the
            code.
          </span>
        </p>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-400 dark:border-slate-700">
              <th className="py-2 pr-3 font-semibold">Usage type</th>
              <th className="py-2 px-3 text-right font-semibold">Quantity</th>
              <th className="py-2 px-3 font-semibold">Unit</th>
              <th className="py-2 px-3 text-right font-semibold">Rate</th>
              <th className="py-2 px-3 text-right font-semibold">Cost</th>
              <th className="py-2 pl-3 text-right font-semibold">Share</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((r) => (
              <tr key={r.usage_type}>
                <td className="py-2 pr-3">
                  <span className="flex items-center gap-2">
                    <span className={`h-2 w-2 flex-none rounded-full ${bandFor(r.usage_type).dot}`} />
                    <span className="font-mono text-xs text-slate-700 dark:text-slate-300">
                      {r.usage_type}
                    </span>
                  </span>
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-slate-600 dark:text-slate-400">
                  {qty(r.usage_qty)}
                </td>
                <td className="py-2 px-3 text-xs text-slate-500 dark:text-slate-400">
                  {unitFor(r.usage_type, r.usage_unit)}
                </td>
                <td
                  className="py-2 px-3 text-right font-mono text-xs tabular-nums text-slate-500 dark:text-slate-400"
                  title="Cost divided by quantity — should match Lambda's published price for this usage type"
                >
                  {rate(r.cost_usd, r.usage_qty)}
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-slate-900 dark:text-slate-100">
                  {usd(r.cost_usd)}
                </td>
                <td className="py-2 pl-3 text-right tabular-nums text-xs text-slate-500">
                  {share(r).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
        <strong>Quantity</strong> is in that row&apos;s own unit, which is why only the{' '}
        <span className="font-mono">Request</span> row is a call count. <strong>Rate</strong> is cost
        divided by quantity and matches Lambda&apos;s list price — $0.0000041667 per GB-second of
        reserved concurrency, $0.0000166667 per GB-second executed, $0.20 per million requests.{' '}
        <strong>Share</strong> is the row&apos;s percentage of this service&apos;s cost in the window,
        so it says which line is worth acting on.
      </p>
    </>
  );
}

/**
 * Daily cost as columns.
 *
 * Columns rather than a line: daily cost is a set of discrete billed amounts,
 * and a line would imply interpolation between days that does not exist. The
 * y-axis starts at zero, because truncating it exaggerates variation — the
 * classic way a cost chart misleads.
 */
function DailyChart({ data }: { data: { period_date: string; cost_usd: string; invocations: number }[] }) {
  if (data.length === 0) {
    return <p className="text-sm text-slate-400">No daily data in this window.</p>;
  }
  const max = Math.max(...data.map((d) => Number(d.cost_usd)), 0);
  const scale = max > 0 ? max : 1;
  const labels = [0, Math.floor((data.length - 1) / 2), data.length - 1].filter(
    (v, i, a) => a.indexOf(v) === i,
  );

  return (
    <div>
      <div className="flex items-baseline justify-between text-xs text-slate-400">
        <span>peak {usd(String(max))}/day</span>
        <span>{data.length} days</span>
      </div>
      <div className="mt-2 flex h-32 items-end gap-[2px]">
        {data.map((d) => {
          const pct = (Number(d.cost_usd) / scale) * 100;
          return (
            <div
              key={d.period_date}
              className="flex-1 rounded-t bg-brand-500/70 transition-colors hover:bg-brand-500"
              style={{ height: `${Math.max(pct, Number(d.cost_usd) > 0 ? 1 : 0)}%` }}
              title={`${d.period_date}\n${usd(d.cost_usd)}\n${d.invocations.toLocaleString()} invocations`}
            />
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-slate-400">
        {labels.map((i) => (
          <span key={i}>{data[i].period_date.slice(5)}</span>
        ))}
      </div>
    </div>
  );
}

/**
 * Mean billed duration per call — derived, not measured.
 *
 * GB-seconds = configured memory (in GB) x billed duration, summed over every
 * call, so dividing back out by the memory setting and the call count recovers
 * the average runtime. This is the answer to "why does one call cost that
 * much" whenever the bill is dominated by GB-seconds instead of requests:
 * nothing is mispriced, the function simply runs for a long time.
 *
 * Renders nothing unless all three inputs are present, since a guessed memory
 * size would turn a derivation into a fabrication.
 */
function DurationNote({
  gbSeconds,
  invocations,
  memoryMb,
  timeoutS,
}: {
  gbSeconds?: string;
  invocations?: number;
  memoryMb?: number | null;
  timeoutS?: number | null;
}) {
  const gbs = Number(gbSeconds ?? NaN);
  if (!memoryMb || !invocations || Number.isNaN(gbs) || gbs <= 0) return null;

  const fnSeconds = gbs / (memoryMb / 1024);
  const avg = fnSeconds / invocations;
  const nearTimeout = timeoutS ? avg > timeoutS * 0.7 : false;

  return (
    <p className="mt-3 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
      {invocations.toLocaleString()} calls consumed{' '}
      {gbs.toLocaleString(undefined, { maximumFractionDigits: 0 })} GB-seconds. At {memoryMb} MB
      that works out to roughly{' '}
      <strong className="text-slate-700 dark:text-slate-200">
        {avg >= 1 ? `${avg.toFixed(1)} s` : `${(avg * 1000).toFixed(0)} ms`}
      </strong>{' '}
      of billed runtime per call
      {timeoutS ? ` against a ${timeoutS}s timeout` : ''}. Duration, not the request charge, is what
      this service pays for — requests are $0.20 per million, runtime is $0.0000166667 per GB-second.
      {nearTimeout &&
        ' Average runtime is close to the timeout, so calls are likely doing heavy work rather than occasionally hanging.'}
    </p>
  );
}

function Config({ spec, resourceCount }: { spec?: ResourceSpecRow; resourceCount: number }) {
  if (!spec) {
    return (
      <p className="text-sm text-slate-400">
        No spec recorded. The sync reads this from the Lambda API, so a service that is billed but
        not listed has usually been deleted while still incurring cost.
      </p>
    );
  }
  const rows: Array<[string, string]> = [
    ['Function', spec.resource_key],
    ['Memory', spec.memory_mb ? `${spec.memory_mb} MB` : '—'],
    ['Architecture', spec.architecture ?? '—'],
    ['Timeout', spec.timeout_s ? `${spec.timeout_s}s` : '—'],
    ['Runtime', spec.runtime ?? '—'],
    ['Package', spec.package_type ?? '—'],
    ['Region', spec.region ?? '—'],
    ['Billed resources', String(resourceCount)],
  ];
  return (
    <>
      <dl className="grid grid-cols-2 gap-y-2 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-slate-500 dark:text-slate-400">{k}</dt>
            <dd className="break-all font-mono text-xs text-slate-900 dark:text-slate-100">{v}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
        Configuration comes from the Lambda API (memory, timeout, runtime, architecture). Cost and
        quantity come from Cost Explorer&apos;s resource-level data, which is a separate source — the
        two are joined on the function name, not reported together by AWS.
      </p>
      {spec.memory_utilisation_note && (
        <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          {spec.memory_utilisation_note}
        </p>
      )}
    </>
  );
}

/**
 * Who owns this, and where its code lives.
 *
 * Sourced from the service catalogue rather than from cloud tags: the
 * catalogue is already maintained, whereas tagging every Lambda with an owner
 * is a migration nobody has run. Cloud tags stay as a fallback below.
 *
 * The absence of an entry is information too — a service AWS bills us for
 * that nobody has registered is exactly the thing to chase — so it renders a
 * prompt rather than nothing at all.
 */
function Ownership({ catalog, tags }: { catalog?: ServiceCatalogRow | null; tags: Record<string, string> }) {
  const tagOwner = OWNER_TAG_KEYS.map((k) => tags[k]).find(Boolean);

  if (!catalog) {
    return (
      <div className="space-y-2 text-sm">
        <p className="text-slate-500 dark:text-slate-400">
          Not in the service catalogue. AWS bills us for it, but no team, repository or description
          is registered against the name — worth adding, since that is what turns a cost row into
          something someone can act on.
        </p>
        {tagOwner && (
          <p className="text-slate-600 dark:text-slate-300">
            Cloud tag says owner: <strong>{tagOwner}</strong>
          </p>
        )}
      </div>
    );
  }

  const rows: Array<[string, React.ReactNode]> = [
    ['Name', catalog.name],
    ['Owner', catalog.owner_team ?? tagOwner ?? 'unassigned'],
    [
      'Repository',
      catalog.repo_url ? (
        <a
          href={catalog.repo_url}
          target="_blank"
          rel="noreferrer"
          className="text-brand-600 hover:underline dark:text-brand-400"
        >
          {catalog.repo_full_name ?? catalog.repo_url}
        </a>
      ) : (
        '—'
      ),
    ],
    ['Group', catalog.service_group ?? '—'],
    ['Lifecycle', catalog.lifecycle_stage ?? '—'],
  ];

  return (
    <div className="space-y-3">
      {catalog.description && (
        <p className="text-sm text-slate-600 dark:text-slate-300">{catalog.description}</p>
      )}
      <dl className="grid grid-cols-2 gap-y-2 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-slate-500 dark:text-slate-400">{k}</dt>
            <dd className="break-all text-slate-900 dark:text-slate-100">{v}</dd>
          </div>
        ))}
      </dl>
      {Object.keys(catalog.links ?? {}).length > 0 && (
        <div className="flex flex-wrap gap-3">
          {Object.entries(catalog.links).map(([label, href]) => (
            <LinkRow key={label} href={href}>
              {label}
            </LinkRow>
          ))}
        </div>
      )}
    </div>
  );
}

export default function UnitCostDetailModal({ serviceSlug, env, period, onClose }: Props) {
  const { data, isLoading, error } = useUnitCostDetail(serviceSlug ?? undefined, {
    period,
    ...(env ? { env } : {}),
  });

  // Escape closes, matching every other overlay in the app. Bound
  // unconditionally so the listener is added and removed on the same renders
  // regardless of whether a service is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const summary = data?.summary;
  const spec = data?.specs?.[0];

  const owner = useMemo(() => {
    const tags = spec?.tags ?? {};
    for (const k of OWNER_TAG_KEYS) if (tags[k]) return tags[k];
    return null;
  }, [spec]);

  if (!serviceSlug) return null;

  const chips = [summary?.env, summary?.aws_service, spec?.architecture, owner ? `owner: ${owner}` : null]
    .filter(Boolean)
    .concat([`last ${period}`]) as string[];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="my-8 w-full max-w-4xl rounded-2xl bg-white shadow-2xl dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
          <div>
            <h3 className="font-mono text-base font-semibold text-slate-900 dark:text-slate-100">
              {serviceSlug}
            </h3>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {chips.map((c) => (
                <span
                  key={c}
                  className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 transition-colors hover:text-slate-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-6">
          {error && (
            <p className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
              Failed to load this service: {(error as Error).message}
            </p>
          )}

          {isLoading && !data ? (
            <p className="py-12 text-center text-sm text-slate-500">Loading…</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <Kpi label="Cost" value={usd(summary?.total_cost_usd)} />
                <Kpi label="Invocations" value={(summary?.invocations ?? 0).toLocaleString()} />
                <Kpi
                  label="$ / invocation"
                  value={perInvocation(summary?.cost_per_invocation_usd)}
                  muted={!summary?.cost_per_invocation_usd}
                />
                <Kpi
                  label="$ / 1000 calls"
                  value={per1000(summary?.cost_per_1000_usd)}
                  muted={!summary?.cost_per_1000_usd}
                />
                {/* The marginal figure. Where reserved capacity dominates it is
                    orders of magnitude below the all-in number, and that gap is
                    the finding rather than a rounding artefact. */}
                <Kpi
                  label="$ / 1000 (traffic)"
                  value={per1000(summary?.usage_cost_per_1000_usd)}
                  muted={!summary?.usage_cost_per_1000_usd}
                />
              </div>

              {summary && Number(summary.reserved_cost_usd) > 0 && (
                <p className="rounded-xl border border-slate-200/60 p-3 text-xs leading-relaxed text-slate-600 dark:border-slate-700 dark:text-slate-300">
                  Split of {usd(summary.total_cost_usd)}:{' '}
                  <strong className="text-amber-600 dark:text-amber-400">
                    {usd(summary.reserved_cost_usd)} reserved capacity
                  </strong>{' '}
                  (billed on the clock, unaffected by traffic) and{' '}
                  <strong>{usd(summary.usage_cost_usd)} traffic</strong> (execution, requests,
                  transfer). Only the second half responds to call volume, which is why the two
                  per-100 figures differ.
                </p>
              )}

              {summary && summary.invocations === 0 && (
                <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                  No invocations were billed in this window, so there is no cost per call to report.
                  The cost above is real — usually reserved capacity or storage — which is why it is
                  shown as unavailable rather than as zero.
                </p>
              )}

              <Section
                title="Where the money goes"
                hint="Cost split by AWS usage type. This is what explains an unexpected cost per invocation."
              >
                <UsageTypes rows={data?.usage_types ?? []} />
                <DurationNote
                  gbSeconds={summary?.gb_seconds}
                  invocations={summary?.invocations}
                  memoryMb={spec?.memory_mb}
                  timeoutS={spec?.timeout_s}
                />
              </Section>

              <Section title="Daily cost" hint="Hover a column for that day's cost and invocation count.">
                <DailyChart data={data?.daily ?? []} />
              </Section>

              <Section
                title="Ownership"
                hint="From the service catalogue — who to talk to before changing anything here."
              >
                <Ownership catalog={data?.catalog} tags={spec?.tags ?? {}} />
              </Section>

              <div className="grid gap-6 lg:grid-cols-2">
                <Section title="Configuration">
                  <Config spec={spec} resourceCount={summary?.resource_count ?? 0} />
                </Section>

                <Section title="Investigate">
                  {spec ? (
                    <div className="flex flex-col gap-2">
                      {/* AWS links first: they are guaranteed to resolve for any
                          Lambda. Datadog only has this function if the log
                          forwarder happens to be attached to it, which for a
                          good number of these it is not. */}
                      <LinkRow href={cloudWatchLogsUrl(spec.resource_key, spec.region)}>
                        CloudWatch logs
                      </LinkRow>
                      <LinkRow href={lambdaConsoleUrl(spec.resource_key, spec.region)}>
                        AWS Lambda console
                      </LinkRow>
                      <LinkRow href={datadogLogsUrl(spec.resource_key)}>Datadog logs</LinkRow>
                      <LinkRow href={datadogMetricsUrl(spec.resource_key)}>Datadog metrics</LinkRow>
                      <p className="mt-1 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                        CloudWatch always has this function&apos;s logs. Datadog only does if the
                        forwarder is attached — several of these functions send no logs or metrics to
                        Datadog at all, so an empty Datadog view is expected rather than a fault.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">Links need a recorded spec.</p>
                  )}
                  <div className="mt-4 space-y-1 text-xs text-slate-500 dark:text-slate-400">
                    {summary?.last_seen && <p>Last billed {summary.last_seen}</p>}
                    {summary?.account_id && <p className="font-mono">account {summary.account_id}</p>}
                    {Object.keys(spec?.tags ?? {}).length > 0 && (
                      <p className="break-all font-mono">
                        tags:{' '}
                        {Object.entries(spec!.tags)
                          .map(([k, v]) => `${k}=${v}`)
                          .join(' · ')}
                      </p>
                    )}
                  </div>
                </Section>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
